import type { KycView } from "../api/types.js";
import type { IconId } from "../lib/icon.js";

/**
 * DCTA-216 (docs/design/v3/10-welcome.md) — the first-run welcome's model, pure.
 *
 * Three steps, one line each: how to start the assistant, what to say, what to check before signing. The example is ONE
 * sentence that works on this customer's own accounts and payees, and step 3 shows the card the bank builds from it — the
 * same words, names, digit groups and figures (§3, checked against the seed by test/welcome-model.spec.ts; never built from
 * a nickname: Wei Jie's seed holds an injection string as one). A customer whose payments are stopped is never taught a
 * payment: two steps and a read. Nothing here is spoken (§8).
 */
export type WelcomeStep = "start" | "say" | "check";

export interface ExampleOp {
  readonly icon: IconId;
  readonly title: string;
  readonly sub: string;
  readonly amount: string;
}

export interface ExampleCard {
  readonly count: string;
  readonly total: string;
  readonly ops: readonly ExampleOp[];
  readonly sign: string;
}

export interface Example {
  readonly say: string;
  readonly card?: ExampleCard;
}

/** Anyone not in the table, and anyone whose payments are stopped: a read, no card — never an invented payee or figure. */
export const FALLBACK: Example = { say: "What's my balance?" };

const send = (name: string, registered: string, digits: string, amount: string): ExampleOp => ({
  icon: "i-send",
  title: `Send to ${name}`,
  sub: `${registered} ···${digits} · from Everyday Savings`,
  amount,
});

/** §3 — keyed by the seed's customer_id. */
export const EXAMPLES: Readonly<Record<string, Example>> = {
  cus_dk005: {
    say: "Send 75 to Mum and 100 to Sarah",
    card: {
      count: "2 operations",
      total: "S$175.00",
      ops: [send("Mum", "KOH LAY HOON", "5711", "S$75.00"), send("Sarah", "SARAH LIM", "5812", "S$100.00")],
      sign: "Sign all 2 · S$175.00",
    },
  },
  cus_wj001: {
    say: "Pay my card bill and send 50 to Mum",
    card: {
      count: "2 operations",
      total: "S$462.00",
      ops: [
        { icon: "i-card", title: "Pay Platinum Card", sub: "CC-2026-09 · ···6012 · from Everyday Savings", amount: "S$412.00" },
        send("Mum", "LIM SIOK HOON", "3321", "S$50.00"),
      ],
      sign: "Sign all 2 · S$462.00",
    },
  },
  cus_ml002: {
    say: "Send 100 to my son",
    card: { count: "1 operation", total: "S$100.00", ops: [send("Wei Jie", "TAN WEI JIE", "4102", "S$100.00")], sign: "Sign · S$100.00" },
  },
  cus_rd004: {
    say: "Send 50 to Mum",
    card: { count: "1 operation", total: "S$50.00", ops: [send("Mum", "SITI AMINAH BINTE OSMAN", "6612", "S$50.00")], sign: "Sign · S$50.00" },
  },
  cus_nch003: FALLBACK,
};

/** Money can move: the identity check is up to date or only due (24c: lapsed or unreadable has stopped it). */
function paymentsOk(kyc: KycView | undefined): boolean {
  return kyc !== undefined && kyc.money_movement_allowed && (kyc.status === "verified" || kyc.status === "refresh_due");
}

export function exampleFor(customerId: string, kyc: KycView | undefined): Example {
  const example = EXAMPLES[customerId];
  if (example === undefined || example.card === undefined) return example ?? FALLBACK;
  return paymentsOk(kyc) ? example : FALLBACK;
}

export function stepsFor(customerId: string, kyc: KycView | undefined): readonly WelcomeStep[] {
  return exampleFor(customerId, kyc).card === undefined ? ["start", "say"] : ["start", "say", "check"];
}

/** §4 W3–W5; W3b with the microphone off — the walkthrough never teaches a gesture that will not work. */
export function lineFor(step: WelcomeStep, voiceOff: boolean): string {
  if (step === "start") return voiceOff ? "Tap to type." : "Swipe up to speak. Tap to type.";
  if (step === "say") return "Say it in one sentence.";
  return "Check the bank's card, then sign with your passkey.";
}

/** §4 W10: the one polite status on a step change — the count and the line, then the example in words. */
export function saidFor(index: number, total: number, step: WelcomeStep, voiceOff: boolean, example: Example): string {
  const head = `${String(index + 1)} of ${String(total)}. ${lineFor(step, voiceOff)}`;
  if (step === "say") return `${head} For example: ${example.say}`;
  const card = example.card;
  if (step === "check" && card !== undefined) {
    return `${head} For example: ${card.ops.map((o) => `${o.title}, ${o.amount}`).join(". ")}. ${card.sign.replace(" · ", ", ")}.`;
  }
  return head;
}

/** §1: by itself only when it has not been seen here, on Home, with judge mode off and nothing open or being signed. */
export function shouldOpen(g: { readonly seen: boolean; readonly onHome: boolean; readonly judge: boolean; readonly busy: boolean }): boolean {
  return !g.seen && g.onHome && !g.judge && !g.busy;
}

export const WELCOME_KEY = "dcta.welcome.seen";

interface Store {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * Seen, once per device and customer (`dcta.welcome.seen.<customer_id>`). Storage blocked or a private window: kept in
 * memory, so it shows once per visit and never twice in one (§0.9). Never throws.
 */
export class WelcomeSeen {
  readonly #storage: () => Store;
  readonly #memory = new Set<string>();

  constructor(storage: () => Store) {
    this.#storage = storage;
  }

  has(customerId: string): boolean {
    if (this.#memory.has(customerId)) return true;
    try {
      return this.#storage().getItem(`${WELCOME_KEY}.${customerId}`) === "1";
    } catch {
      return false;
    }
  }

  mark(customerId: string): void {
    this.#memory.add(customerId);
    try {
      this.#storage().setItem(`${WELCOME_KEY}.${customerId}`, "1");
    } catch {
      // Not persisted: seen for this visit.
    }
  }
}
